import { generateHmacSignature, getTimestamp } from './hmac';
import {
  getStoredTokens,
  saveTokens,
  clearTokens,
  isTokenExpired,
  decodeIdToken,
  getDeviceId,
} from './tokenStorage';

/**
 * The header the backend stamps on every response (S11.4). Surfacing it turns
 * "it broke" into a support case someone can look up: the same id is on every
 * log line the request produced.
 */
export const REQUEST_ID_HEADER = 'x-request-id';

/**
 * An API failure that remembers how to be reported.
 *
 * `message` stays the human-readable text screens already render, so nothing
 * that catches an Error needs to change; the status and correlation id are
 * additive for the surfaces that want to show them.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly requestId?: string,
    /**
     * The parsed error body. A 409 carries what the screen needs to recover —
     * the existing enrollment's id, or the terms that replaced the ones shown —
     * and a caller reading `error.response.data` for it, axios-style, found
     * nothing: this client is fetch.
     */
    readonly body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * PRT-17-FR-01 / PRT-BR-P01-08 — who to tell when the session is dead.
 *
 * A 401 used to surface as one screen's error message while the header went on
 * showing a signed-in user: the tokens were stale, Redux never heard. The
 * client cannot dispatch to the store itself — the store's slices import this
 * module, so the import would be circular — so the app registers a listener
 * instead, and this module only announces.
 */
type AuthExpiredListener = () => void;
const authExpiredListeners = new Set<AuthExpiredListener>();

/** Register for "the session is no longer valid". Returns the unsubscribe. */
export function onAuthExpired(listener: AuthExpiredListener): () => void {
  authExpiredListeners.add(listener);
  return () => {
    authExpiredListeners.delete(listener);
  };
}

/** Forget the dead tokens and tell whoever is listening. */
function expireSession(): void {
  clearTokens();
  authExpiredListeners.forEach((listener) => listener());
}

/**
 * DEC-A01-08 — the server wants a fresh MFA challenge.
 *
 * Announced the way an expired session is, for the same circular-import reason.
 * The caller still gets its ApiError and the request is dropped: nothing here
 * queues or retries it, because a step-up must never replay a prepared
 * mutation (DEC-A01-16).
 */
export interface StepUpChallenge {
  /** The refused request's verb, so a read can offer a reload. */
  method: string;
  reasonCode?: string;
}
type StepUpListener = (challenge: StepUpChallenge) => void;
const stepUpListeners = new Set<StepUpListener>();

/** Register for "the server asked for MFA". Returns the unsubscribe. */
export function onStepUpRequired(listener: StepUpListener): () => void {
  stepUpListeners.add(listener);
  return () => {
    stepUpListeners.delete(listener);
  };
}

export function announceStepUpRequired(challenge: StepUpChallenge): void {
  stepUpListeners.forEach((listener) => listener(challenge));
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const HMAC_SECRET = process.env.REACT_APP_HMAC_SECRET || '';

export interface ApiRequestOptions {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  path: string;
  /** A function is read after any token refresh, for a body that carries a token. */
  body?: any;
  requiresAuth?: boolean;
  requiresHmac?: boolean;
}

/**
 * Make a secure API request with HMAC signature and Cognito authentication
 */
export async function makeSecureRequest<T = any>(options: ApiRequestOptions): Promise<T> {
  const { method, path, requiresAuth = true, requiresHmac = true } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add authentication if required
  if (requiresAuth) {
    let { idToken, refreshToken } = getStoredTokens();

    // No usable tokens is a dead session too — another tab may have signed
    // out — so the UI is told, not only the caller.
    if (!idToken) {
      expireSession();
      throw new Error('Authentication required. Please log in.');
    }

    // Auto-refresh if the idToken is expired or about to expire
    if (isTokenExpired(idToken)) {
      if (!refreshToken) {
        expireSession();
        throw new Error('Authentication required. Please log in.');
      }
      try {
        const refreshed = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // The backend's Cognito client has a secret (CTO decision 12 Oct
          // 2026), and a refresh then needs the pool username for its
          // SECRET_HASH — the refresh token itself does not reveal it.
          body: JSON.stringify({
            refreshToken,
            username: decodeIdToken(idToken)?.['cognito:username'],
          }),
        });
        if (!refreshed.ok) throw new Error('Token refresh failed');
        const data = await refreshed.json();
        saveTokens({
          idToken: data.idToken,
          accessToken: data.accessToken,
          refreshToken: refreshToken,
          expiresIn: data.expiresIn,
        });
        idToken = data.idToken;
      } catch {
        expireSession();
        throw new Error('Session expired. Please log in again.');
      }
    }

    headers['Authorization'] = `Bearer ${idToken}`;
    headers['X-Device-Id'] = getDeviceId();
  }

  const { body: bodyOption = null } = options;
  const body = typeof bodyOption === 'function' ? bodyOption() : bodyOption;

  // Add HMAC signature if required
  if (requiresHmac) {
    const timestamp = getTimestamp();
    const signature = generateHmacSignature(method, path, body, timestamp, HMAC_SECRET);

    headers['X-HMAC-Signature'] = signature;
    headers['X-HMAC-Timestamp'] = timestamp;
  }

  // Make the request
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  // A 401 on an authenticated call means the server no longer accepts this
  // session (revoked, expired past refresh, signed out elsewhere). Stop showing
  // a signed-in user (PRT-BR-P01-08). Unauthenticated calls are excluded: a
  // wrong password at sign-in is a 401 too, and it ends no session.
  if (response.status === 401 && requiresAuth) {
    expireSession();
  }

  // Handle response
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 403 && errorData.code === 'STEP_UP_REQUIRED') {
      announceStepUpRequired({ method, reasonCode: errorData.reasonCode });
    }
    throw new ApiError(
      errorData.message || `API request failed with status ${response.status}`,
      response.status,
      response.headers.get(REQUEST_ID_HEADER) || undefined,
      errorData,
    );
  }

  return response.json();
}

/**
 * Make a GET request
 */
export function apiGet<T = any>(
  path: string,
  requiresAuth = true,
  requiresHmac = true,
): Promise<T> {
  return makeSecureRequest<T>({
    method: 'GET',
    path,
    requiresAuth,
    requiresHmac,
  });
}

/**
 * Make a POST request
 */
export function apiPost<T = any>(
  path: string,
  body: any,
  requiresAuth = true,
  requiresHmac = true,
): Promise<T> {
  return makeSecureRequest<T>({
    method: 'POST',
    path,
    body,
    requiresAuth,
    requiresHmac,
  });
}

/**
 * Make a PUT request
 */
export function apiPut<T = any>(
  path: string,
  body: any,
  requiresAuth = true,
  requiresHmac = true,
): Promise<T> {
  return makeSecureRequest<T>({
    method: 'PUT',
    path,
    body,
    requiresAuth,
    requiresHmac,
  });
}

/**
 * Make a PATCH request
 */
export function apiPatch<T = any>(
  path: string,
  body: any,
  requiresAuth = true,
  requiresHmac = true,
): Promise<T> {
  return makeSecureRequest<T>({
    method: 'PATCH',
    path,
    body,
    requiresAuth,
    requiresHmac,
  });
}

/**
 * Make a DELETE request
 */
export function apiDelete<T = any>(
  path: string,
  requiresAuth = true,
  requiresHmac = true,
): Promise<T> {
  return makeSecureRequest<T>({
    method: 'DELETE',
    path,
    requiresAuth,
    requiresHmac,
  });
}

/**
 * Public API request (no auth, no HMAC)
 */
export function apiPublic<T = any>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: any,
): Promise<T> {
  return makeSecureRequest<T>({
    method,
    path,
    body,
    requiresAuth: false,
    requiresHmac: false,
  });
}
